// Earlier months (docs/mastermind-admin-plan.md 3.3), folded, newest first, read only: each action with how
// it went, its tick, the member's note, and "Worked out" on a month closed before the accountability log
// or copied from the old site. Correct changes how an action went, with a reason, which the history keeps
// and the log marks "Updated" (hub.correct_outcome); Done on an action carried on is refused there. The months that
// came from the old site are noted here, once (the critique's admin 6), not as a job in Needs you.
import { useState } from 'react'
import { Button, Card, Dialog, Field, Select, TextArea, useAdmin } from '../ui/index.js'
import { MarkIcon } from '../../products/mastermind/Log.jsx'
import { dm, wdm } from '../../products/mastermind/log.js'
import { MM, PANEL } from './words.js'
import { correctOutcome } from './data.js'
import { Said, tickedLine } from './ThisMonth.jsx'
import { copiedNote } from './section.js'

const OUTCOMES = ['done', 'part_done', 'not_done']

function CorrectDialog({ action, ctx, onClose, onDone }) {
  const { api } = useAdmin()
  const [outcome, setOutcome] = useState(action.result === 'set_aside' ? 'done' : action.result || 'done')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  async function go() {
    if (busy) return
    if (!reason.trim()) { setProblem(MM.reasonNeeded); return }
    setBusy(true); setProblem(null)
    try {
      await correctOutcome(api, { action: action.action_id, outcome, reason }, { ...ctx, carriedTo: action.carried_to })
      await onDone(MM.corrected)
    } catch (e) {
      setProblem(e.message)
      setBusy(false)
    }
  }
  return (
    <Dialog title={MM.correctTitle} onClose={onClose} busy={busy}
      footer={<><Button kind="ink" busy={busy} onClick={go}>{MM.correctSave}</Button><Button onClick={onClose} disabled={busy}>{PANEL.cancel}</Button></>}>
      <p className="amx-dialog-quote">{action.body}</p>
      <Field label={MM.correctField}>
        {props => <Select {...props} value={outcome} onChange={setOutcome} options={OUTCOMES.map(o => ({ value: o, label: MM.outcomes[o] }))} />}
      </Field>
      <Field label={MM.reasonField} hint={MM.reasonHint} error={problem}>
        {props => <TextArea {...props} value={reason} onChange={v => { setReason(v); setProblem(null) }} rows={3} maxLength={300} />}
      </Field>
      <p className="amx-muted">{MM.correctNote}</p>
    </Dialog>
  )
}

const markOf = a => (a.result === 'set_aside' ? 'set_aside' : a.carried_to ? 'carried' : a.result === 'done' ? (a.hops > 0 ? 'done_late' : 'done') : a.result)

export function EarlierMonths({ log, first, ctx, onChanged }) {
  const closed = (log.months || []).filter(m => m.closed_at)
  const [correcting, setCorrecting] = useState(null)
  const [line, setLine] = useState(null)
  const copied = copiedNote(log)
  return (
    <Card id="mm-earlier" title={MM.earlierH} className="amx-earlier">
      {!closed.length && <p className="amx-muted">{MM.noEarlier}</p>}
      {copied && <p className="amx-muted amx-copied-note">{copied}</p>}
      {closed.map(m => (
        <details key={m.cycle_id} className="amx-month">
          <summary className="amx-month-head">
            <span>{MM.earlierLine({
              month: m.month, setDay: m.set_at ? wdm(m.set_at) : dm(m.started_at), atSession: Boolean(m.set_at_session),
              reviewDay: m.reviewed_at ? wdm(m.reviewed_at) : null, reviewedAtSession: m.reviewed_at_session,
            })}</span>
            {m.copied && <span className="ad-tag">{MM.copiedTag}</span>}
          </summary>
          <ol className="amx-actions">
            {(m.actions || []).map(a => {
              const mark = markOf(a)
              return (
                <li key={a.action_id} className="amx-action amx-action--earlier">
                  <span className={`amx-mark amx-mark--${mark}`}><MarkIcon mark={mark} /></span>
                  <div className="amx-action-main">
                    <span className="amx-action-body">{a.body}</span>
                    <span className="amx-action-lines">
                      <span className="amx-strong">{a.carried_to ? `${MM.marks[a.result] || ''}. ${MM.carriedTo(a.carried_to)}` : MM.marks[a.result] || ''}</span>
                      {(a.ticked || a.done_at) && <span>{tickedLine(a, first)}</span>}
                      {a.worked_out && <span className="ad-tag" title={MM.workedOutTitle}>{MM.workedOut}</span>}
                      {a.result === 'set_aside' && a.set_aside_reason && <span>{MM.setAsideTag(a.set_aside_reason)}</span>}
                    </span>
                    {a.note && <span className="amx-note">{MM.note(first, a.note)}</span>}
                  </div>
                  {a.result !== 'set_aside' && (
                    <div className="amx-action-side">
                      <Button size="xs" onClick={() => { setCorrecting({ ...a, month: m.month }); setLine(null) }} aria-label={`${MM.correct}: ${a.body}`}>{MM.correct}</Button>
                    </div>
                  )}
                </li>
              )
            })}
            {!(m.actions || []).length && <li className="amx-muted">{MM.noActions}</li>}
          </ol>
        </details>
      ))}
      <Said line={line} />
      {correcting && <CorrectDialog action={correcting} ctx={ctx} onClose={() => setCorrecting(null)}
        onDone={async text => { setCorrecting(null); await onChanged(); setLine({ text }) }} />}
    </Card>
  )
}
